import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, XCircle, Eye, Clock, Search, AlertTriangle,
} from 'lucide-react';
import { useT } from '@/store/i18n';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { QuestionDetailModal } from '@/components/QuestionDetailModal';
import { api } from '@/lib/api';
import type { Question, SubjectGroup, GradeLevel } from '@/types';

interface QuestionsPayload {
  questions?: Question[];
  pagination?: { total?: number; page?: number; pages?: number };
}

export function Approvals() {
  const t = useT();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [rejectTarget, setRejectTarget] = useState<number[] | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [detailId, setDetailId] = useState<number | null>(null);

  const meta = useQuery({
    queryKey: ['metadata'],
    queryFn: () => api<{ data?: { subject_groups?: SubjectGroup[]; grade_levels?: GradeLevel[] } }>('metadata/all.php').then((r) => r.data ?? {}),
    staleTime: 5 * 60_000,
  });
  const subjects = meta.data?.subject_groups ?? [];
  const grades = meta.data?.grade_levels ?? [];

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set('status', 'pending');
    p.set('page', String(page));
    p.set('limit', '20');
    if (search) p.set('search', search);
    if (subject) p.set('subject_group_id', subject);
    if (grade) p.set('grade_level_id', grade);
    return p.toString();
  }, [page, search, subject, grade]);

  const list = useQuery({
    queryKey: ['approvals', params],
    queryFn: () => api<{ data?: QuestionsPayload } & QuestionsPayload>(`questions/index.php?${params}`).then((r) => r.data ?? r),
  });

  const questions = list.data?.questions ?? [];
  const total = list.data?.pagination?.total ?? questions.length;

  const approveOne = useMutation({
    mutationFn: (id: number) =>
      api('questions/approve.php', { method: 'POST', json: { question_id: id, action: 'approve' } }),
    onSuccess: () => {
      toast.success(t('approvals.success_approve'));
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (e: Error) => toast.error(t('approvals.fail_approve'), e.message),
  });

  const rejectOne = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api('questions/approve.php', { method: 'POST', json: { question_id: id, action: 'reject', comment: reason } }),
    onSuccess: () => {
      toast.success(t('approvals.success_reject'));
      qc.invalidateQueries({ queryKey: ['approvals'] });
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
    },
    onError: (e: Error) => toast.error(t('approvals.fail_reject'), e.message),
  });

  const allChecked = questions.length > 0 && questions.every((q) => selected.has(q.id));
  const someChecked = !allChecked && questions.some((q) => selected.has(q.id));

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) questions.forEach((q) => next.delete(q.id));
      else questions.forEach((q) => next.add(q.id));
      return next;
    });
  }
  function toggleOne(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function bulkApprove() {
    if (selected.size === 0) return;
    const ok = await confirmDialog({
      title: `${t('approvals.bulk_approve_title')} ${selected.size} ${t('approvals.items')}?`,
      description: t('approvals.bulk_approve_desc'),
      confirmLabel: t('approvals.bulk_approve_confirm'),
    });
    if (!ok) return;
    for (const id of selected) {
      try {
        await approveOne.mutateAsync(id);
      } catch {
        // toast already shown
      }
    }
    setSelected(new Set());
  }

  function startBulkReject() {
    if (selected.size === 0) return;
    setRejectTarget(Array.from(selected));
    setRejectReason('');
  }

  async function submitReject() {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      toast.warning(t('approvals.reject_reason_required'));
      return;
    }
    for (const id of rejectTarget) {
      try {
        await rejectOne.mutateAsync({ id, reason: rejectReason });
      } catch {
        // shown
      }
    }
    setRejectTarget(null);
    setRejectReason('');
    setSelected(new Set());
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('approvals.title')}
        description={`${t('approvals.subtitle')} · ${t('approvals.pending_count')} ${total.toLocaleString('th-TH')} ${t('approvals.items')}`}
        actions={
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
            <Clock className="w-4 h-4" />
            <span>{t('approvals.pending_count')} {total.toLocaleString('th-TH')}</span>
          </div>
        }
      />

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            leftIcon={<Search className="w-4 h-4" />}
            placeholder={t('approvals.search')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <Select value={subject} onChange={(e) => { setSubject(e.target.value); setPage(1); }}>
            <option value="">{t('filter.all_subjects')}</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
          <Select value={grade} onChange={(e) => { setGrade(e.target.value); setPage(1); }}>
            <option value="">{t('filter.all_grades')}</option>
            {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </Select>
        </div>
      </Card>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky top-16 z-10 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 shadow-md animate-slide-down">
          <Badge tone="warning">
            <span className="font-semibold">{selected.size}</span> {t('approvals.selected')}
          </Badge>
          <div className="flex-1" />
          <Button
            size="sm"
            leftIcon={<CheckCircle2 className="w-4 h-4" />}
            onClick={bulkApprove}
            loading={approveOne.isPending}
          >
            {t('approvals.approve_selected')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            leftIcon={<XCircle className="w-4 h-4" />}
            onClick={startBulkReject}
          >
            {t('approvals.reject_selected')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            {t('btn.cancel')}
          </Button>
        </div>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <th className="w-10 px-4 py-3">
                  <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={toggleAll}
                  />
                </th>
                <th className="text-left font-semibold px-4 py-3">{t('approvals.col.question')}</th>
                <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t('approvals.col.creator')}</th>
                <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">{t('approvals.col.subject')}</th>
                <th className="text-left font-semibold px-4 py-3 hidden lg:table-cell">{t('approvals.col.grade')}</th>
                <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">{t('approvals.col.submitted')}</th>
                <th className="text-right font-semibold px-4 py-3">{t('approvals.col.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                  </tr>
                ))
              ) : questions.length === 0 ? (
                <tr><td colSpan={7}>
                  <EmptyState
                    title={t('approvals.empty')}
                    description={t('approvals.empty_desc')}
                  />
                </td></tr>
              ) : (
                questions.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-50/70">
                    <td className="px-4 py-3 align-top">
                      <Checkbox
                        checked={selected.has(q.id)}
                        onChange={() => toggleOne(q.id)}
                      />
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <div className="text-slate-900 font-medium line-clamp-2">{q.question_text || '—'}</div>
                      {q.topic && (
                        <div className="text-xs text-slate-500 truncate mt-0.5">{q.topic}</div>
                      )}
                      <div className="mt-1.5 lg:hidden">
                        {q.subject_name && <Badge tone="brand">{q.subject_name}</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-600 text-xs">
                      {q.creator_name ?? q.created_by_name ?? '—'}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {q.subject_name ? <Badge tone="brand">{q.subject_name}</Badge> : '—'}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-600 text-xs">{q.grade_name ?? '—'}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-500 text-xs">
                      {new Date(q.created_at).toLocaleDateString('th-TH', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" aria-label={t('btn.view')} onClick={() => setDetailId(q.id)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<CheckCircle2 className="w-3.5 h-3.5" />}
                          onClick={async () => {
                            const ok = await confirmDialog({
                              title: t('approvals.approve_one'),
                              confirmLabel: t('btn.approve'),
                            });
                            if (ok) approveOne.mutate(q.id);
                          }}
                        >
                          {t('btn.approve')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<XCircle className="w-3.5 h-3.5 text-red-600" />}
                          onClick={() => {
                            setRejectTarget([q.id]);
                            setRejectReason('');
                          }}
                        >
                          <span className="text-red-600">{t('btn.reject')}</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {questions.length > 0 && (
          <div className="px-4 border-t border-slate-100">
            <Pagination page={page} pageSize={20} total={total} onPageChange={setPage} />
          </div>
        )}
      </Card>

      <QuestionDetailModal questionId={detailId} onClose={() => setDetailId(null)} />

      {/* Reject reason modal */}
      <Modal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title={`${t('approvals.reject_modal_title')} ${rejectTarget?.length ?? 0} ${t('approvals.items')}`}
        description={t('approvals.reject_modal_desc')}
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejectTarget(null)}>{t('btn.cancel')}</Button>
            <Button
              variant="danger"
              onClick={submitReject}
              loading={rejectOne.isPending}
              leftIcon={<AlertTriangle className="w-4 h-4" />}
            >
              {t('approvals.confirm_reject')}
            </Button>
          </>
        }
      >
        <Textarea
          label={t('approvals.reject_reason_label')}
          placeholder={t('approvals.reject_reason_ph')}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={5}
          autoFocus
        />
      </Modal>
    </div>
  );
}
